import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { OrderServiceItem, ServiceProduct } from '../../../client/services';

type VariationOption = {
  id: string;
  labelEn?: string;
  labelAr?: string;
  priceDelta?: number | string;
  activeState?: boolean;
};

type ProductOption = {
  id: string;
  labelEn?: string;
  labelAr?: string;
  basePrice?: number | string;
  fromPrice?: number | string;
  maxQty?: number | string;
  activeState?: boolean;
  variations?: VariationOption[];
  service_vendor?: { id?: string; name?: string; isActive?: boolean };
};

const toMoney = (value: unknown) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const formatQar = (value: unknown) => `${toMoney(value).toFixed(2)} QAR`;

/** Variation extra (priceDelta) — keep 0 / 45 as signed extras, e.g. +0.00 QAR */
const formatExtraQar = (value: unknown) => {
  const n = toMoney(value);
  const sign = n >= 0 ? '+' : '';
  return `${sign}${n.toFixed(2)} QAR`;
};

const unitPriceFor = (
  product?: ProductOption | null,
  variation?: VariationOption | null
) =>
  Math.round(
    (toMoney(product?.basePrice) + toMoney(variation?.priceDelta)) * 100
  ) / 100;

type Props = {
  open: boolean;
  orderId: string;
  onClose: () => void;
  onAdded: () => void;
};

const AddOrderServiceDialog = ({ open, orderId, onClose, onAdded }: Props) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState<ProductOption[]>([]);
  const [selected, setSelected] = useState<ProductOption | null>(null);
  const [variationId, setVariationId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [qtyOfProduct, setQtyOfProduct] = useState(1);

  useEffect(() => {
    if (!open) {
      return;
    }
    setSelected(null);
    setVariationId('');
    setQuantity(1);
    setQtyOfProduct(1);
    setLoading(true);
    // service-product find Joi only allows search/categoryId/vendorId + paging
    ServiceProduct.find({
      query: { $limit: 1000, $skip: 0 },
    })
      .then((res: any) => {
        const rows = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
            ? res
            : [];
        setOptions(
          rows
            .filter(
              (r: ProductOption) => r?.id && r.activeState !== false
            )
            .sort((a: ProductOption, b: ProductOption) =>
              String(a.labelEn || '').localeCompare(String(b.labelEn || ''))
            )
        );
      })
      .catch((err: any) => {
        setOptions([]);
        toast.error(
          err?.data?.message ||
            err?.message ||
            'Failed to load services'
        );
      })
      .finally(() => setLoading(false));
  }, [open]);

  const activeVariations = useMemo(
    () =>
      (selected?.variations || []).filter(
        (v) => v?.id && v.activeState !== false
      ),
    [selected]
  );

  const maxQty = Math.max(1, Number(selected?.maxQty || 1) || 1);
  const maxQtyOfProduct = Math.max(1, maxQty * Math.max(1, quantity));
  const selectedVariation =
    activeVariations.find((v) => v.id === variationId) || null;
  const basePrice = toMoney(selected?.basePrice);
  const extraPrice = toMoney(selectedVariation?.priceDelta);
  const unitPrice = unitPriceFor(selected, selectedVariation);
  const lineTotal = unitPrice * Math.max(1, quantity);

  useEffect(() => {
    if (!selected) {
      setVariationId('');
      return;
    }
    if (activeVariations.length === 1) {
      setVariationId(activeVariations[0].id);
    } else if (
      variationId &&
      !activeVariations.some((v) => v.id === variationId)
    ) {
      setVariationId('');
    }
  }, [selected, activeVariations, variationId]);

  const handleAdd = async () => {
    if (!orderId || !selected?.id) {
      toast.error('Select a service');
      return;
    }
    if (activeVariations.length > 0 && !variationId) {
      toast.error('Select a package / variation');
      return;
    }
    const nextQty = Math.max(1, Math.floor(Number(quantity) || 1));
    const nextProductQty = Math.max(
      1,
      Math.min(maxQtyOfProduct, Math.floor(Number(qtyOfProduct) || 1))
    );
    setSaving(true);
    try {
      await OrderServiceItem.create({
        orderId,
        productId: selected.id,
        variationId: variationId || undefined,
        quantity: nextQty,
        qtyOfProduct: nextProductQty,
      });
      toast.success('Service added');
      onAdded();
      onClose();
    } catch (err: any) {
      toast.error(
        err?.data?.message ||
          err?.data?.detail ||
          err?.message ||
          'Failed to add service'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth='sm'
    >
      <DialogTitle>Add service</DialogTitle>
      <DialogContent
        sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        {loading ? (
          <CircularProgress size={28} sx={{ alignSelf: 'center', my: 2 }} />
        ) : (
          <>
            <Autocomplete
              options={options}
              value={selected}
              onChange={(_e, value) => setSelected(value)}
              getOptionLabel={(opt) => {
                const name =
                  opt?.labelEn ||
                  opt?.labelAr ||
                  opt?.service_vendor?.name ||
                  opt?.id ||
                  '';
                return `${name} — from ${formatQar(opt?.basePrice)}`;
              }}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              renderOption={(props, opt) => (
                <li {...props} key={opt.id}>
                  {opt.labelEn || opt.labelAr || opt.id} — from{' '}
                  {formatQar(opt.basePrice)}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label='Service product' />
              )}
            />
            {activeVariations.length > 0 ? (
              <TextField
                select
                label='Package / variation'
                value={variationId}
                onChange={(e) => setVariationId(e.target.value)}
                helperText='Extra price on top of base (e.g. +0.00 / +45.00 QAR)'
              >
                {activeVariations.map((v) => (
                  <MenuItem key={v.id} value={v.id}>
                    {v.labelEn || v.labelAr || v.id} —{' '}
                    {formatExtraQar(v.priceDelta)}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
            <Stack direction='row' spacing={2} alignItems='flex-start'>
              <TextField
                fullWidth
                label='Qty'
                type='number'
                value={quantity}
                onChange={(e) => {
                  const next = Math.max(1, Number(e.target.value) || 1);
                  setQuantity(next);
                  setQtyOfProduct((prev) =>
                    Math.min(Math.max(1, prev), maxQty * next)
                  );
                }}
                inputProps={{ min: 1, step: 1 }}
              />
              <TextField
                fullWidth
                label='Qty of product'
                type='number'
                value={qtyOfProduct}
                onChange={(e) =>
                  setQtyOfProduct(
                    Math.max(
                      1,
                      Math.min(maxQtyOfProduct, Number(e.target.value) || 1)
                    )
                  )
                }
                inputProps={{ min: 1, max: maxQtyOfProduct, step: 1 }}
                helperText={
                  selected
                    ? `Max ${maxQtyOfProduct} (maxQty ${maxQty} × qty)`
                    : undefined
                }
              />
            </Stack>
            {selected ? (
              <Typography variant='body2' color='text.secondary'>
                Base {formatQar(basePrice)}
                {selectedVariation
                  ? ` · Extra ${formatExtraQar(extraPrice)}`
                  : ''}{' '}
                · Unit {formatQar(unitPrice)} · Line total {formatQar(lineTotal)}
              </Typography>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleAdd}
          disabled={saving || loading || !selected}
        >
          {saving ? 'Adding…' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddOrderServiceDialog;
